import { useState } from "react";

import { Button, ErrorBanner, Input, Modal, Select, Spinner } from "@/components/ui";
import { toApiError } from "@/lib/api";
import { useT } from "@/lib/i18n";
import { useCreateTimeOff } from "@/lib/queries";

/** Atalhos de duração: "vou sair às 15h" precisa de 2 toques, não de um
 *  formulário com data e hora de início e fim. */
const PRESETS = [
  { label: "30 min", minutos: 30 },
  { label: "1 hora", minutos: 60 },
  { label: "2 horas", minutos: 120 },
  { label: "Resto do dia", minutos: 0 },
];

export default function TimeOffModal({
  open,
  onClose,
  professionalId,
  defaultDate,
}: {
  open: boolean;
  onClose: () => void;
  professionalId: number;
  defaultDate: string;
}) {
  const t = useT();
  const [date, setDate] = useState(defaultDate);
  const [start, setStart] = useState(() => {
    const d = new Date();
    d.setMinutes(Math.ceil(d.getMinutes() / 15) * 15, 0, 0);
    return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
  });
  const [minutos, setMinutos] = useState(60);
  const [reason, setReason] = useState("");

  const create = useCreateTimeOff();
  const error = create.isError ? toApiError(create.error) : null;

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const inicio = new Date(`${date}T${start}:00`);
    const fim =
      minutos === 0
        ? new Date(`${date}T23:59:00`)
        : new Date(inicio.getTime() + minutos * 60_000);

    create.mutate(
      {
        professional: professionalId,
        starts_at: inicio.toISOString(),
        ends_at: fim.toISOString(),
        reason: reason.trim(),
      },
      {
        onSuccess: () => {
          setReason("");
          onClose();
        },
      },
    );
  };

  return (
    <Modal open={open} onClose={onClose} title={t("modal.blockTime")}>
      <form onSubmit={submit} className="space-y-4">
        <p className="text-sm text-steel-muted">{t("modal.timeOffHint")}</p>

        <div className="grid grid-cols-2 gap-3">
          <Input
            label={t("modal.day")}
            type="date"
            required
            value={date}
            onChange={(e) => setDate(e.target.value)}
          />
          <Input
            label={t("modal.startingAt")}
            type="time"
            step={900}
            required
            value={start}
            onChange={(e) => setStart(e.target.value)}
          />
        </div>

        <div>
          <span className="eyebrow mb-2 block text-steel-dim">{t("modal.howLong")}</span>
          <div className="grid grid-cols-2 gap-2">
            {PRESETS.map((p) => (
              <button
                key={p.label}
                type="button"
                onClick={() => setMinutos(p.minutos)}
                aria-pressed={minutos === p.minutos}
                className={
                  minutos === p.minutos
                    ? "rounded-[4px] border border-nitro-500 bg-nitro-500 py-3 text-sm font-semibold text-carbon-900"
                    : "rounded-[4px] border border-line py-3 text-sm text-steel transition hover:border-steel-dim"
                }
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>

        <Select
          label={t("modal.reasonOptional")}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
        >
          <option value="">{t("modal.noReason")}</option>
          <option value={t("modal.lunch")}>{t("modal.lunch")}</option>
          <option value={t("modal.personal")}>{t("modal.personal")}</option>
          <option value={t("modal.doctor")}>{t("modal.doctor")}</option>
          <option value={t("modal.dayOff")}>{t("modal.dayOff")}</option>
        </Select>

        <ErrorBanner message={error?.message} />

        <div className="flex gap-2">
          <Button type="button" variant="secondary" onClick={onClose}>{t("common.back")}</Button>
          <Button type="submit" variant="racing" className="flex-1" disabled={create.isPending}>
            {create.isPending ? (
              <Spinner className="size-4 border-carbon-900/30 border-t-carbon-900" />
            ) : (
              "Bloquear"
            )}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
